if (!customElements.get('nutra-nav-drawer')) {
  customElements.define(
    'nutra-nav-drawer',
    class NutraNavDrawer extends HTMLElement {
      constructor() {
        super();
        this.closeTimer = null;
        this.lastFocusedElement = null;
        this.onOpen = this.open.bind(this);
        this.onClose = this.close.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onResize = this.handleResize.bind(this);
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
      }

      connectedCallback() {
        this.trigger = document.querySelector('[data-nutra-menu-open]');
        this.panel = this.querySelector('.nutra-nav-drawer__panel');
        this.closeButtons = Array.from(this.querySelectorAll('[data-nutra-menu-close]'));
        this.links = Array.from(this.querySelectorAll('.nutra-nav-drawer__link, .nutra-nav-drawer__sublink'));
        this.categoriesToggle = this.querySelector('[data-nutra-drawer-categories]');
        this.categoriesItem = this.categoriesToggle?.closest('.nutra-nav-drawer__item');
        this.onToggleCategories = this.toggleCategories.bind(this);

        this.trigger?.addEventListener('click', this.onOpen);
        this.closeButtons.forEach((button) => button.addEventListener('click', this.onClose));
        this.links.forEach((link) => link.addEventListener('click', this.onClose));
        this.categoriesToggle?.addEventListener('click', this.onToggleCategories);
        this.desktopQuery.addEventListener('change', this.onResize);
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener('click', this.onOpen);
        this.categoriesToggle?.removeEventListener('click', this.onToggleCategories);
        document.removeEventListener('keydown', this.onKeydown);
        this.desktopQuery.removeEventListener('change', this.onResize);
        window.clearTimeout(this.closeTimer);
      }

      get isOpen() {
        return this.classList.contains('is-open');
      }

      open(event) {
        if (this.desktopQuery.matches) return;
        if (this.isOpen) {
          this.close();
          return;
        }

        event?.preventDefault();
        window.clearTimeout(this.closeTimer);
        this.lastFocusedElement = document.activeElement;
        this.hidden = false;
        this.resetCategories();
        document.querySelector('nutra-search-drawer')?.close?.();
        document.body.classList.add('nutra-menu-open');
        this.trigger?.setAttribute('aria-expanded', 'true');
        document.addEventListener('keydown', this.onKeydown);

        requestAnimationFrame(() => {
          this.classList.add('is-open');
          window.setTimeout(() => this.panel?.focus(), 180);
        });
      }

      close() {
        if (!this.isOpen && this.hidden) return;

        this.classList.remove('is-open');
        document.body.classList.remove('nutra-menu-open');
        this.trigger?.setAttribute('aria-expanded', 'false');
        document.removeEventListener('keydown', this.onKeydown);

        this.closeTimer = window.setTimeout(() => {
          this.hidden = true;
          this.resetCategories();
          this.lastFocusedElement?.focus();
        }, 280);
      }

      toggleCategories(event) {
        event.preventDefault();
        event.stopPropagation();
        const isOpen = this.categoriesItem?.classList.toggle('is-open');
        this.categoriesToggle?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      resetCategories() {
        this.categoriesItem?.classList.remove('is-open');
        this.categoriesToggle?.setAttribute('aria-expanded', 'false');
      }

      handleKeydown(event) {
        if (event.key === 'Escape') this.close();
      }

      handleResize() {
        if (this.desktopQuery.matches) this.close();
      }
    }
  );
}

if (!customElements.get('nutra-categories-menu')) {
  customElements.define(
    'nutra-categories-menu',
    class NutraCategoriesMenu extends HTMLElement {
      constructor() {
        super();
        this.onToggle = this.toggle.bind(this);
        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onSelect = this.handleSelect.bind(this);
        this.onResize = this.handleResize.bind(this);
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
      }

      connectedCallback() {
        this.trigger = this.querySelector('[data-nutra-categories-trigger]');
        this.panel = this.querySelector('[data-nutra-categories-panel]');
        this.items = Array.from(this.querySelectorAll('[data-nutra-category]'));
        this.grids = Array.from(this.querySelectorAll('[data-nutra-category-panel]'));
        this.wrap = this.closest('.nutra-header-wrap');

        if (this.panel && this.wrap && this.panel.parentElement !== this.wrap) {
          this.wrap.appendChild(this.panel);
        }

        this.trigger?.addEventListener('click', this.onToggle);
        this.items.forEach((item) => item.addEventListener('click', this.onSelect));
        this.desktopQuery.addEventListener('change', this.onResize);
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener('click', this.onToggle);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        this.desktopQuery.removeEventListener('change', this.onResize);
      }

      get isOpen() {
        return this.classList.contains('is-open');
      }

      toggle(event) {
        if (!this.desktopQuery.matches) return;
        event?.preventDefault();
        event?.stopPropagation();
        if (this.isOpen) {
          this.close();
          return;
        }
        this.open();
      }

      open() {
        if (!this.desktopQuery.matches || this.isOpen) return;

        document.querySelector('nutra-search-drawer')?.close();
        this.panel.hidden = false;
        this.classList.add('is-open');
        this.trigger?.classList.add('is-active');
        this.trigger?.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
      }

      close() {
        if (!this.isOpen) return;

        this.classList.remove('is-open');
        this.trigger?.classList.remove('is-active');
        this.trigger?.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        if (this.panel) this.panel.hidden = true;
      }

      handleSelect(event) {
        const button = event.currentTarget;
        const category = button.getAttribute('data-nutra-category');
        if (!category) return;

        this.items.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        this.grids.forEach((grid) => {
          grid.classList.toggle('is-active', grid.getAttribute('data-nutra-category-panel') === category);
        });
      }

      handleDocumentClick(event) {
        if (this.contains(event.target) || this.panel?.contains(event.target)) return;
        this.close();
      }

      handleKeydown(event) {
        if (event.key === 'Escape') this.close();
      }

      handleResize() {
        if (!this.desktopQuery.matches) this.close();
      }
    }
  );
}
